import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {AuthService} from '../../../../services/auth/auth.service';
import {LoaderService} from '../../../../services/loader/loader.service';
import {StatisticService} from '../../../../services/statistic/statistic.service';
import {ConversionReport} from '../../../../models/statistic/conversion-report';
import {PaginatorComponent} from '../../../layout/paginator/paginator.component';
import {Paginator} from '../../../../tools/pagination/paginator';
import {MetaTitle} from '../../../../decorator/meta-title';
import { DatePipe } from '@angular/common';
import {RouterLink} from '@angular/router';
import {MoneyPipe} from '../../../../pipes/money/money.pipe';
import {CsvExportEvent, CsvExportService} from '../../../../services/csv-export/csv-export.service';
import {SnackbarService} from '../../../../services/snackbar/snackbar.service';
import {Subscription} from 'rxjs';

@Component({
  selector: 'app-conversion-report',
  imports: [
    PaginatorComponent,
    DatePipe,
    RouterLink,
    MoneyPipe
],
  templateUrl: './conversion-report.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './conversion-report.component.scss'
})
@MetaTitle('Conversion report')
export class ConversionReportComponent implements OnInit, OnDestroy {

  private filters: any;

  statistics: ConversionReport[] = [];
  paginator: Paginator = new Paginator();

  private filtersSubscription: Subscription | null = null;
  private fetchSubscription: Subscription | null = null;
  private exportSubscription: Subscription | null = null;
  private downloadSubscription: Subscription | null = null;

  constructor(
    protected loader: LoaderService,
    private statisticService: StatisticService,
    private auth: AuthService,
    private csvExport: CsvExportService,
    private snackbar: SnackbarService
  ) {
  }

  getTimezone(date: string = ''): string | undefined {
    const timezone = this.auth.getConfig()?.timezone;

    if (timezone === null || timezone === undefined) {
      return undefined;
    }

    // The API stamps each date with the affiliate timezone offset, DST included:
    // reuse it so the displayed time always matches the server-side local time
    const offset = date.match(/[+-]\d{2}:?\d{2}$/);
    if (offset) {
      return offset[0];
    }

    return (timezone < 0 ? '-' : '+') + Math.abs(timezone).toString().padStart(2, '0') + '00';
  }

  ngOnInit(): void {
    this.statistics = [];

    this.exportSubscription = this.statisticService.$exportCsv.subscribe(() => this.downloadCsv());

    this.filtersSubscription = this.statisticService.$filters.subscribe((filters: any) => {
      this.filters = filters;
      this.fetchConversions();
    });
  }

  ngOnDestroy(): void {
    this.filtersSubscription?.unsubscribe();
    this.fetchSubscription?.unsubscribe();
    this.exportSubscription?.unsubscribe();
    this.downloadSubscription?.unsubscribe();
    this.statisticService.$exportProgress.next(null);
    this.loader.stopLoading();
  }

  fetchConversions(): void {
    this.loader.startLoading();

    this.fetchSubscription?.unsubscribe();
    this.fetchSubscription = this.statisticService.fetchConversionReport(this.filters, this.paginator)
      .subscribe((statistics: ConversionReport[]) => {
        this.statistics = statistics;
        this.loader.stopLoading();
      });
  }

  /**
   * Exported straight from the API: the CSV covers the whole filtered range,
   * not only the page being displayed.
   */
  downloadCsv(): void {
    if (this.loader.isLoading() || !this.statistics.length || this.statisticService.isExporting()) {
      return;
    }

    if (!this.filters?.startDate || !this.filters?.endDate) {
      return;
    }

    this.statisticService.$exportProgress.next({current: 0, total: 0});

    this.downloadSubscription?.unsubscribe();
    this.downloadSubscription = this.csvExport.exportFromApi({
      endpoint: '/statistics/conversion/download',
      params: this.filters,
      filename: 'conversion-report.csv'
    }).subscribe({
      next: (event: CsvExportEvent) => {
        if (event.type === 'progress') {
          this.statisticService.$exportProgress.next({current: event.current, total: event.total});
          return;
        }

        this.statisticService.$exportProgress.next(null);

        if (event.truncatedChunks > 0) {
          this.snackbar.warning(
            'The export is incomplete: too many conversions for a single day. Please narrow the date range.'
          );
        }
      },
      error: (error: any) => {
        this.statisticService.$exportProgress.next(null);
        this.snackbar.error('CSV export failed. Please try again.', error);
      }
    });
  }

}
